import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { FileText } from "lucide-react";
import { getSupabaseWithSessionHeaders } from "../lib/supabase_server";
import Header from "../components/header";
import Footer from "../components/Footer";

export async function loader({ request }: LoaderFunctionArgs) {
  const { headers, supabase, session } = await getSupabaseWithSessionHeaders({
    request,
  });
  let userName = "";
  let isAdmin = false;
  if (session) {
    const { data } = await supabase
      .from("Users")
      .select("first_name, admin")
      .eq("id", session.user.id)
      .single();
    userName = data?.first_name ?? "";
    isAdmin = Boolean(data?.admin);
  }
  return json(
    { userId: session?.user?.id ?? "", userName, isAdmin },
    { headers },
  );
}

export const meta: MetaFunction = () => {
  return [
    { title: "Terms of Service - BulkBuyPlace" },
    { name: "description", content: "BulkBuyPlace terms of service." },
  ];
};

const sections = [
  {
    title: "Platform Role",
    content:
      "BulkBuyPlace is a marketplace that connects buyers with independent suppliers. We do not sell products directly. All products listed on the platform are offered by third-party suppliers who are solely responsible for their listings, pricing, fulfillment, and quality.",
  },
  {
    title: "Accounts",
    content:
      "You must provide accurate information when creating an account. You are responsible for maintaining the security of your account credentials. We reserve the right to suspend or terminate accounts that violate these terms.",
  },
  {
    title: "Payments",
    content:
      "Transactions for product orders are arranged directly between buyers and suppliers. BulkBuyPlace does not process payments for product purchases. Accepted payment methods (cash, Zelle, check, PayPal) are determined by the supplier.",
  },
  {
    title: "Supplier Subscriptions",
    content:
      "Suppliers may subscribe to paid plans for access to additional platform features. Subscription payments are processed securely through Stripe. Cancellations, upgrades, and downgrades are managed through the billing settings in your store dashboard.",
  },
  {
    title: "Orders and Fulfillment",
    content:
      "Suppliers may accept, partially accept, or reject orders at their discretion. BulkBuyPlace is not responsible for order fulfillment, delivery timelines, or product quality. Any disputes regarding orders should be resolved directly with the supplier.",
  },
  {
    title: "Limitation of Liability",
    content:
      'BulkBuyPlace provides the platform "as is" and makes no warranties regarding the availability, accuracy, or reliability of the service. We are not liable for any damages arising from your use of the platform or transactions with suppliers.',
  },
  {
    title: "Changes to These Terms",
    content:
      "We may update these terms from time to time. Continued use of the platform after changes constitutes acceptance of the revised terms.",
  },
];

export default function TermsPage() {
  const { userId, userName, isAdmin } = useLoaderData<typeof loader>();
  return (
    <div className="flex flex-col min-h-[100dvh] bg-gray-50">
      <Header userId={userId} userName={userName} isAdmin={isAdmin} />

      <main className="flex-1 pt-16">
        {/* Page header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 py-12 md:py-16 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-600 mb-4">
              <FileText className="h-5 w-5" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              Terms of Service
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              Last updated: February 2026
            </p>
          </div>
        </div>

        {/* Sections */}
        <div className="max-w-3xl mx-auto px-4 py-12 md:py-16">
          <div className="space-y-10">
            {sections.map((section, i) => (
              <div key={section.title}>
                <h2 className="text-lg font-semibold text-gray-900">
                  {i + 1}. {section.title}
                </h2>
                <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                  {section.content}
                </p>
              </div>
            ))}

            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {sections.length + 1}. Contact
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-gray-600">
                Questions about these terms? Contact us at{" "}
                <a
                  href="mailto:bulkbuyplace@gmail.com"
                  className="text-orange-600 hover:underline"
                >
                  bulkbuyplace@gmail.com
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
